/**
 * WC-V20-HOST-4 — mute-aware Stream unread math for Firefly / Messenger badges.
 * Bridge mutes (`bridge_user_mutes`) suppress Layer A bell rows; the same
 * channel ids must not drive Firefly teal/`💬` or the audible ding.
 */

export function channelIdFromStreamCid(cid: string | null | undefined): string {
  const raw = String(cid || "").trim();
  if (!raw) return "";
  const idx = raw.indexOf(":");
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

export function isChannelMutedForAlerts(
  channelId: string,
  mutedChannelIds: readonly string[]
): boolean {
  const id = String(channelId || "").trim();
  if (!id) return false;
  return mutedChannelIds.includes(id);
}

export type ChannelUnreadRow = {
  channelId: string;
  unread: number;
};

export type StreamUnreadChannelLike = {
  id?: string | null;
  cid?: string | null;
  countUnread?: () => number;
};

export function rowsFromStreamChannels(
  channels: readonly StreamUnreadChannelLike[]
): ChannelUnreadRow[] {
  return channels.map((ch) => {
    const channelId = String(ch.id || channelIdFromStreamCid(ch.cid) || "").trim();
    const unreadRaw = typeof ch.countUnread === "function" ? Number(ch.countUnread()) : 0;
    return {
      channelId,
      unread: Number.isFinite(unreadRaw) ? unreadRaw : 0,
    };
  });
}

/** Sum unread across channels, skipping bridge-muted channel ids. */
export function sumUnreadExcludingMuted(
  rows: readonly ChannelUnreadRow[],
  mutedChannelIds: readonly string[]
): number {
  const muted = new Set(mutedChannelIds.map((id) => String(id || "").trim()).filter(Boolean));
  let n = 0;
  for (const row of rows) {
    const id = String(row.channelId || "").trim();
    if (!id || muted.has(id)) continue;
    const unread = Number(row.unread);
    if (!Number.isFinite(unread) || unread <= 0) continue;
    n += unread;
  }
  return n;
}
